/**
 * KARAKTER OLUŞTURMA SİHİRBAZI
 *
 * Tasarım kararı: Sihirbaz KURAL UYDURMAZ. Her adım doğrulamayı
 * `packages/rules/builder.ts` içindeki saf fonksiyonlara yaptırır. Böylece
 * "27 puan", "2 beceri", "en az 2 yetenek" gibi kurallar tek yerde yaşar.
 *
 * Canlı önizleme: taslak her değiştiğinde `aggregateCharacter()` çalıştırılır
 * ve AC/HP/DC hesaplanmış hali gösterilir. Kullanıcı yarım bırakıp bıraksa
 * bile sonucu görebilir.
 */

import {
  ABILITY_KEYS,
  ABILITY_NAMES_TR,
  type AbilityKey,
  type AbilityMethod,
  ALIGNMENTS,
  type AsiSlot,
  aggregateCharacter,
  asiSlotsForLevel,
  type CharacterDraft,
  type ContentIndex,
  draftToCharacterInput,
  evaluateAsi,
  evaluatePointBuy,
  evaluateSkillChoices,
  POINT_BUY_BUDGET,
  POINT_BUY_MAX,
  POINT_BUY_MIN,
  pointBuyCost,
  type RaceEntry,
  rollAbilityScores,
  STANDARD_ARRAY,
  sortAbilityScores,
  validateAbilityScores,
  validateDraft,
  xpForLevel,
} from '@tttrpg/rules'
import { useMemo, useState } from 'react'
import { Panel, Tag } from './primitives'
import { rollCheck } from './rollStore'

// ---------------------------------------------------------------------------
// TASLAK
// ---------------------------------------------------------------------------

export function emptyDraft(): CharacterDraft {
  return {
    name: '',
    alignment: '',
    // ⚠️ Başlangıç dizgisi GEÇERLİ bir atama olmalı. Standart Dizgi'de her
    // puan bir kez kullanılır; altı yeteneğe de 8 yazmak geçersizdir ve
    // sihirbazı 2. adımda kilitlerdi.
    abilityMethod: 'standard',
    abilityScores: { str: 10, dex: 15, con: 14, int: 12, wis: 8, cha: 13 },
    raceId: null,
    asiChoices: {},
    choices: {},
    backgroundId: null,
    classId: null,
    level: 1,
    skillProficiencies: [],
    asiSlots: [],
  }
}

const STEPS = [
  { id: 'kimlik', label: 'Kimlik' },
  { id: 'yetenek', label: 'Yetenekler' },
  { id: 'irk', label: 'Irk' },
  { id: 'arkaPlan', label: 'Arka Plan' },
  { id: 'sinif', label: 'Sınıf' },
  { id: 'seviye', label: 'Seviye' },
  { id: 'ozet', label: 'Özet' },
] as const

type StepId = (typeof STEPS)[number]['id']

export interface CharacterBuilderProps {
  content: ContentIndex
  initial?: CharacterDraft
  onCancel: () => void
  onComplete: (draft: CharacterDraft, identity: { name: string; alignment: string }) => void
}

export function CharacterBuilder({
  content,
  initial,
  onCancel,
  onComplete,
}: CharacterBuilderProps) {
  const [draft, setDraft] = useState<CharacterDraft>(initial ?? emptyDraft())
  const [step, setStep] = useState<StepId>('kimlik')

  const validation = useMemo(() => validateDraft(draft, content), [draft, content])
  const stepIndex = STEPS.findIndex((s) => s.id === step)

  /** Bu adımın kendi eksikleri (özet adımı hariç: tüm adımları kapsar). */
  const stepMissing = step === 'ozet' ? [] : (validation.missing[step] ?? [])

  const canAdvance = stepMissing.length === 0

  const patch = (partial: Partial<CharacterDraft>) => setDraft((prev) => ({ ...prev, ...partial }))

  // --- CANLI ÖNİZLEME ---------------------------------------------------
  // Taslak geçerli değilse de hesap yapılır; eksik alanlar varsayılanla
  // doldurulur ama uyarılar gösterilmez (önizleme, sonuç değil).
  const preview = useMemo(() => {
    const input = draftToCharacterInput(draft, content)
    if (input.classes.length === 0) return null
    try {
      return aggregateCharacter(input, content)
    } catch {
      return null
    }
  }, [draft, content])

  return (
    <div className="mx-auto max-w-4xl space-y-4 p-3 pb-24 md:p-6">
      {/* Başlık + adım göstergesi */}
      <header className="panel p-4">
        <div className="flex items-center justify-between gap-3">
          <h1 className="font-display text-xl text-ink">Yeni Karakter</h1>
          <button
            type="button"
            onClick={onCancel}
            className="cursor-pointer rounded border border-line px-3 py-1 text-xs text-ink-muted transition-colors hover:border-blood hover:text-blood"
          >
            Vazgeç
          </button>
        </div>

        <ol className="mt-4 flex flex-wrap gap-1.5">
          {STEPS.map((s, i) => {
            const missing = validation.missing[s.id]?.length ?? 0
            const done = i < stepIndex
            const active = s.id === step
            return (
              <li key={s.id}>
                <button
                  type="button"
                  onClick={() => setStep(s.id)}
                  className={`flex cursor-pointer items-center gap-1.5 rounded border px-2 py-1 text-xs transition-colors ${
                    active
                      ? 'border-gold bg-gold/20 text-gold-bright'
                      : done
                        ? 'border-vital/40 text-vital'
                        : missing > 0
                          ? 'border-line-soft text-ink-faint'
                          : 'border-line-soft text-ink-muted'
                  }`}
                >
                  <span className="tnum">{i + 1}</span>
                  {s.label}
                  {missing > 0 && <span className="text-warn">•</span>}
                </button>
              </li>
            )
          })}
        </ol>
      </header>

      {/* Adım içeriği */}
      <Panel title={STEPS[stepIndex]?.label}>
        {step === 'kimlik' && <StepKimlik draft={draft} patch={patch} />}
        {step === 'yetenek' && <StepYetenek draft={draft} patch={patch} />}
        {step === 'irk' && <StepIrk draft={draft} patch={patch} content={content} />}
        {step === 'arkaPlan' && <StepArkaPlan draft={draft} patch={patch} content={content} />}
        {step === 'sinif' && <StepSinif draft={draft} patch={patch} content={content} />}
        {step === 'seviye' && <StepSeviye draft={draft} patch={patch} content={content} />}
        {step === 'ozet' && <StepOzet draft={draft} content={content} preview={preview} />}
      </Panel>

      {/* Eksikler */}
      {stepMissing.length > 0 && (
        <div className="panel border-warn/40 p-3">
          <div className="text-xs text-warn">Devam etmek için:</div>
          <ul className="mt-1 space-y-0.5">
            {stepMissing.map((m) => (
              <li key={m} className="text-xs text-ink-muted">
                • {m}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Canlı önizleme */}
      {preview && <Onizleme preview={preview} draft={draft} />}

      {/* Gezinme */}
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => setStep(STEPS[Math.max(0, stepIndex - 1)]?.id ?? 'kimlik')}
          disabled={stepIndex === 0}
          className="cursor-pointer rounded border border-line px-3 py-1.5 text-sm text-ink-muted transition-colors hover:border-gold-dim disabled:cursor-not-allowed disabled:opacity-30"
        >
          ← Geri
        </button>

        {step !== 'ozet' ? (
          <button
            type="button"
            onClick={() => setStep(STEPS[Math.min(STEPS.length - 1, stepIndex + 1)]?.id ?? 'ozet')}
            disabled={!canAdvance}
            className="cursor-pointer rounded border border-gold bg-gold/20 px-4 py-1.5 text-sm font-semibold text-gold-bright transition-colors hover:bg-gold/30 disabled:cursor-not-allowed disabled:border-line disabled:bg-surface-2 disabled:text-ink-faint"
          >
            Devam →
          </button>
        ) : (
          <button
            type="button"
            onClick={() => onComplete(draft, { name: draft.name, alignment: draft.alignment })}
            disabled={!validation.canContinue}
            className="cursor-pointer rounded border border-vital bg-vital/20 px-4 py-1.5 text-sm font-semibold text-vital transition-colors hover:bg-vital/30 disabled:cursor-not-allowed disabled:border-line disabled:bg-surface-2 disabled:text-ink-faint"
          >
            Karakteri Oluştur
          </button>
        )}
      </div>
    </div>
  )
}

// ===========================================================================
// ADIM 1 — KİMLİK
// ===========================================================================

function StepKimlik({
  draft,
  patch,
}: {
  draft: CharacterDraft
  patch: (p: Partial<CharacterDraft>) => void
}) {
  return (
    <div className="space-y-4">
      <label className="block">
        <span className="text-sm text-ink-muted">Karakter Adı</span>
        <input
          value={draft.name}
          onChange={(e) => patch({ name: e.target.value })}
          placeholder="örn. Kaelen Veyl"
          className="mt-1 w-full rounded border border-line bg-surface-2 px-3 py-2 text-ink outline-none focus:border-gold-dim"
        />
      </label>

      <div>
        <span className="text-sm text-ink-muted">Hizalama</span>
        <div className="mt-1 flex flex-wrap gap-1.5">
          {ALIGNMENTS.map((a) => (
            <button
              key={a}
              type="button"
              onClick={() => patch({ alignment: a })}
              className={`cursor-pointer rounded border px-2 py-1 text-xs transition-colors ${
                draft.alignment === a
                  ? 'border-gold bg-gold/20 text-gold-bright'
                  : 'border-line-soft bg-surface-2 text-ink-muted hover:border-gold-dim'
              }`}
            >
              {a}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}

// ===========================================================================
// ADIM 2 — YETENEKLER
// ===========================================================================
// ===========================================================================
// ADIM 2 — YETENEKLER
//
// ÜÇ YÖNTEM, ÜÇ FARKLI ETKİLEŞİM. Karıştırmamak için ayrı dallar:
//
//  • Standart Dizgi → her yeteneğe bir <select>. Değiştirince DEĞER TAKAS
//    edilir (15'i alırsan, 15'i verdiğin yetenek 13'ü alır). Takas mantığı
//    "boş havuz takibi"nden çok daha basit ve yanlışlık üretemez.
//  • Puan Havuzu    → −/+ düğmeleri, maliyet gösterilir, bütçe çubuğu.
//  • 4d6 Yukarı At  → zar at, sonuçları yeteneklere ata.
// ===========================================================================

function StepYetenek({
  draft,
  patch,
}: {
  draft: CharacterDraft
  patch: (p: Partial<CharacterDraft>) => void
}) {
  const [rolled, setRolled] = useState<number[]>([])
  const scores = draft.abilityScores
  const method = draft.abilityMethod
  const pointBuy = evaluatePointBuy(scores)

  const base: Record<AbilityKey, number> = {
    str: 8,
    dex: 8,
    con: 8,
    int: 8,
    wis: 8,
    cha: 8,
  }

  const setMethod = (m: AbilityMethod) => {
    if (m === 'standard') {
      patch({
        abilityMethod: m,
        abilityScores: { str: 10, dex: 15, con: 14, int: 12, wis: 8, cha: 13 },
      })
      return
    }
    if (m === 'pointBuy') {
      patch({ abilityMethod: m, abilityScores: base })
      return
    }
    setRolled(rollAbilityScores())
    patch({ abilityMethod: m, abilityScores: base })
  }

  /** Standart Dizgi: iki yetenek arasında değer takası. */
  const swapStandard = (key: AbilityKey, nextValue: number) => {
    const current = scores[key]
    if (current === nextValue) return
    const holder = ABILITY_KEYS.find((k) => scores[k] === nextValue)
    patch({
      abilityScores: holder
        ? { ...scores, [key]: nextValue, [holder]: current }
        : { ...scores, [key]: nextValue },
    })
  }

  const nudge = (key: AbilityKey, delta: number) => {
    const next = scores[key] + delta
    if (next < POINT_BUY_MIN || next > POINT_BUY_MAX) return
    patch({ abilityScores: { ...scores, [key]: next } })
  }

  const assignRolled = (key: AbilityKey, value: number) => {
    if (scores[key] === value) return
    const holder = ABILITY_KEYS.find((k) => scores[k] === value)
    patch({
      abilityScores: holder
        ? { ...scores, [key]: value, [holder]: scores[key] }
        : { ...scores, [key]: value },
    })
  }

  const sortedRolls = sortAbilityScores(rolled)

  return (
    <div className="space-y-4">
      <MethodPicker method={method} onPick={setMethod} />

      {method === 'pointBuy' && (
        <div className="panel p-3">
          <div className="flex items-center justify-between text-sm">
            <span className="text-ink-muted">Kalan Puan</span>
            <span
              className={`tnum font-display text-lg ${
                pointBuy.remaining < 0 ? 'text-blood' : 'text-gold-bright'
              }`}
            >
              {pointBuy.remaining}
            </span>
          </div>
          <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-void">
            <div
              className="h-full bg-gold transition-[width]"
              style={{
                width: `${Math.max(0, Math.min(100, (pointBuy.spent / POINT_BUY_BUDGET) * 100))}%`,
              }}
            />
          </div>
          {pointBuy.error && <p className="mt-1 text-[0.65rem] text-blood">{pointBuy.error}</p>}
        </div>
      )}

      {method === 'rolled' && (
        <div className="panel p-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-ink-muted">4d6 Yukarı At (3 en yüksek)</span>
            <button
              type="button"
              onClick={() => setRolled(rollAbilityScores())}
              className="cursor-pointer rounded border border-gold-dim px-2 py-1 text-xs text-gold hover:bg-gold/10"
            >
              Zar At
            </button>
          </div>
          {sortedRolls.length === 0 ? (
            <p className="mt-2 text-xs text-ink-faint">Zar atmak için butona bas.</p>
          ) : (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {sortedRolls.map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => {
                    const free = ABILITY_KEYS.find((k) => scores[k] === POINT_BUY_MIN)
                    if (free) assignRolled(free, v)
                  }}
                  className="tnum cursor-pointer rounded border border-line bg-surface-3 px-2 py-1 text-sm text-ink hover:border-gold-dim"
                >
                  {v}
                </button>
              ))}
            </div>
          )}
          <p className="mt-1.5 text-[0.65rem] text-ink-faint">
            Puana tıkla → boş bir yeteneğe atanır. Aşağıdan elle de düzenleyebilirsin.
          </p>
        </div>
      )}

      {/* Yetenek satırları */}
      <div className="space-y-1.5">
        {ABILITY_KEYS.map((key) => {
          const score = scores[key]

          if (method === 'standard') {
            return (
              <div
                key={key}
                className="flex items-center gap-3 rounded border border-line-soft bg-surface-2 px-3 py-2"
              >
                <span className="w-24 text-sm text-ink">{ABILITY_NAMES_TR[key]}</span>
                <select
                  value={score}
                  onChange={(e) => swapStandard(key, Number(e.target.value))}
                  className="tnum rounded border border-line bg-surface-3 px-2 py-1 text-ink outline-none focus:border-gold-dim"
                >
                  {STANDARD_ARRAY.map((v) => (
                    <option key={v} value={v}>
                      {v}
                    </option>
                  ))}
                </select>
                <span className="tnum ml-auto text-sm text-ink-muted">{mod(score)}</span>
              </div>
            )
          }

          const max = method === 'rolled' ? 18 : POINT_BUY_MAX
          const min = method === 'rolled' ? 3 : POINT_BUY_MIN
          return (
            <div
              key={key}
              className="flex items-center gap-3 rounded border border-line-soft bg-surface-2 px-3 py-2"
            >
              <span className="w-24 text-sm text-ink">{ABILITY_NAMES_TR[key]}</span>
              <button
                type="button"
                onClick={() => nudge(key, -1)}
                disabled={score <= min}
                className="tnum w-7 cursor-pointer rounded border border-line bg-surface-3 text-ink-muted disabled:cursor-not-allowed disabled:opacity-30"
              >
                −
              </button>
              <span className="tnum w-8 text-center font-display text-lg text-ink">{score}</span>
              <button
                type="button"
                onClick={() => nudge(key, 1)}
                disabled={score >= max}
                className="tnum w-7 cursor-pointer rounded border border-line bg-surface-3 text-ink-muted disabled:cursor-not-allowed disabled:opacity-30"
              >
                +
              </button>
              <span className="tnum ml-auto text-sm text-ink-muted">{mod(score)}</span>
              {method === 'pointBuy' && (
                <span className="tnum w-16 text-right text-[0.65rem] text-ink-faint">
                  {pointBuyCost(score) === Number.POSITIVE_INFINITY
                    ? '—'
                    : `${pointBuyCost(score)} puan`}
                </span>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

function MethodPicker({
  method,
  onPick,
}: {
  method: AbilityMethod
  onPick: (m: AbilityMethod) => void
}) {
  const options: { id: AbilityMethod; label: string; hint: string }[] = [
    { id: 'standard', label: 'Standart Dizgi', hint: '15, 14, 13, 12, 10, 8' },
    { id: 'pointBuy', label: 'Puan Havuzu', hint: '27 puan, 8–15 arası' },
    { id: 'rolled', label: '4d6 Yukarı At', hint: '6 set at, kendin dağıt' },
  ]
  return (
    <div className="grid gap-2 sm:grid-cols-3">
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          onClick={() => onPick(o.id)}
          className={`cursor-pointer rounded border p-2 text-left transition-colors ${
            method === o.id
              ? 'border-gold bg-gold/20'
              : 'border-line-soft bg-surface-2 hover:border-gold-dim'
          }`}
        >
          <div className={`text-sm ${method === o.id ? 'text-gold-bright' : 'text-ink'}`}>
            {o.label}
          </div>
          <div className="text-[0.65rem] text-ink-faint">{o.hint}</div>
        </button>
      ))}
    </div>
  )
}

function mod(score: number): string {
  const m = Math.floor((score - 10) / 2)
  return m >= 0 ? `+${m}` : `${m}`
}

// ===========================================================================
// ADIM 3 — IRK
// ===========================================================================

function StepIrk({
  draft,
  patch,
  content,
}: {
  draft: CharacterDraft
  patch: (p: Partial<CharacterDraft>) => void
  content: ContentIndex
}) {
  const races = Object.values(content.races)
  const selected = draft.raceId ? content.races[draft.raceId] : null

  return (
    <div className="space-y-4">
      <div className="grid max-h-72 grid-cols-2 gap-1.5 overflow-y-auto sm:grid-cols-3 lg:grid-cols-4">
        {races.map((race) => {
          const active = draft.raceId === race.id
          const empty = race.features.length === 0
          return (
            <button
              key={race.id}
              type="button"
              onClick={() => patch({ raceId: race.id, choices: {}, asiChoices: {} })}
              className={`cursor-pointer rounded border p-2 text-left transition-colors ${
                active
                  ? 'border-gold bg-gold/20'
                  : 'border-line-soft bg-surface-2 hover:border-gold-dim'
              }`}
            >
              <div className={`text-sm ${active ? 'text-gold-bright' : 'text-ink'}`}>
                {race.name}
              </div>
              <div className="text-[0.6rem] text-ink-faint">
                {race.size} · {race.speed} ft
                {race.darkvision > 0 ? ` · KV ${race.darkvision}` : ''}
              </div>
              {empty && <div className="mt-0.5 text-[0.6rem] text-warn">metin bekleniyor</div>}
            </button>
          )
        })}
      </div>

      {selected && <RaceDetail race={selected} draft={draft} patch={patch} />}
    </div>
  )
}

function RaceDetail({
  race,
  draft,
  patch,
}: {
  race: RaceEntry
  draft: CharacterDraft
  patch: (p: Partial<CharacterDraft>) => void
}) {
  const unlocked = race.choices.filter((g) => (g.grantedAtLevel ?? 1) <= draft.level)
  const locked = race.choices.filter((g) => (g.grantedAtLevel ?? 1) > draft.level)

  const toggle = (groupId: string, optionId: string) => {
    const current = draft.choices[groupId] ?? []
    const group = race.choices.find((g) => g.id === groupId)
    if (!group) return
    const max = group.max
    if (current.includes(optionId)) {
      patch({ choices: { ...draft.choices, [groupId]: current.filter((i) => i !== optionId) } })
      return
    }
    if (current.length >= max) {
      // en eskiyi düşür (kullanıcıyı zorlamadan)
      patch({ choices: { ...draft.choices, [groupId]: [...current.slice(1), optionId] } })
      return
    }
    patch({ choices: { ...draft.choices, [groupId]: [...current, optionId] } })
  }

  return (
    <div className="space-y-3 border-t border-line-soft pt-3">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="font-display text-base text-ink">{race.name}</h3>
        <Tag>{race.size}</Tag>
        <Tag>{race.speed} ft</Tag>
        {race.acBase !== 10 && <Tag tone="gold">zirhsiz AC tabanı {race.acBase}</Tag>}
        {race.darkvision > 0 && <Tag tone="gold">karanlık görüş {race.darkvision}</Tag>}
      </div>

      {/* Irk yetenek bonusu (+1 seçimi) */}
      {(race.abilityBonuses.dex !== undefined || race.abilityBonuses.str !== undefined) && (
        <div className="text-xs text-ink-muted">
          Irk bonusu:{' '}
          {Object.entries(race.abilityBonuses).map(([k, v]) => (
            <span key={k} className="mr-2 text-gold">
              {ABILITY_NAMES_TR[k as AbilityKey]} {v && v > 0 ? `+${v}` : v}
            </span>
          ))}
        </div>
      )}

      {race.asiChoiceOptions.length > 0 && (
        <div>
          <div className="text-sm text-ink-muted">Ek +1 hangi yeteneğe?</div>
          <div className="mt-1 flex flex-wrap gap-1.5">
            {race.asiChoiceOptions.map((key) => {
              const active = (draft.asiChoices[key] ?? 0) > 0
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() =>
                    patch({
                      asiChoices: active
                        ? { ...draft.asiChoices, [key]: 0 }
                        : { ...draft.asiChoices, [key]: 1 },
                    })
                  }
                  className={`cursor-pointer rounded border px-2 py-1 text-xs transition-colors ${
                    active
                      ? 'border-gold bg-gold/20 text-gold-bright'
                      : 'border-line-soft bg-surface-2 text-ink-muted hover:border-gold-dim'
                  }`}
                >
                  {ABILITY_NAMES_TR[key]}
                </button>
              )
            })}
          </div>
        </div>
      )}

      {/* Seçim grupları */}
      {unlocked.map((group) => {
        const chosen = draft.choices[group.id] ?? []
        return (
          <div key={group.id}>
            <div className="flex items-center justify-between">
              <span className="text-sm text-ink">{group.labelTr}</span>
              <span
                className={`tnum text-[0.65rem] ${
                  chosen.length >= group.min ? 'text-vital' : 'text-warn'
                }`}
              >
                {chosen.length}/{group.min}
              </span>
            </div>
            {group.pending ? (
              <p className="mt-1 rounded border border-line-soft border-dashed bg-surface-2 px-2 py-1.5 text-[0.7rem] text-ink-faint">
                Bu seçimin listesi henüz eklenmedi.
              </p>
            ) : (
              <div className="mt-1 flex flex-wrap gap-1.5">
                {group.options.map((o) => {
                  const active = chosen.includes(o.id)
                  return (
                    <button
                      key={o.id}
                      type="button"
                      onClick={() => toggle(group.id, o.id)}
                      title={o.description}
                      className={`cursor-pointer rounded border px-2 py-1 text-xs transition-colors ${
                        active
                          ? 'border-gold bg-gold/20 text-gold-bright'
                          : 'border-line-soft bg-surface-2 text-ink-muted hover:border-gold-dim'
                      }`}
                    >
                      {o.nameTr}
                    </button>
                  )
                })}
              </div>
            )}
          </div>
        )
      })}

      {locked.length > 0 && (
        <p className="text-[0.65rem] text-ink-faint">
          {locked.length} seçim seviye kilitli — karakter seviyesi yükselince açılır.
        </p>
      )}

      {/* Seviye 1 özellikleri */}
      {race.features.filter((f) => (f.grantedAtLevel ?? 1) <= draft.level).length > 0 && (
        <details className="text-xs">
          <summary className="cursor-pointer text-ink-muted">
            Özellikler ({race.features.filter((f) => (f.grantedAtLevel ?? 1) <= draft.level).length}
            )
          </summary>
          <ul className="mt-1 space-y-1">
            {race.features
              .filter((f) => (f.grantedAtLevel ?? 1) <= draft.level)
              .map((f) => (
                <li key={f.id}>
                  <span className="text-ink">{f.name}</span>{' '}
                  <span className="text-ink-faint">— {f.description}</span>
                </li>
              ))}
          </ul>
        </details>
      )}
    </div>
  )
}

// ===========================================================================
// ADIM 4 — ARKA PLAN
// ===========================================================================

function StepArkaPlan({
  draft,
  patch,
  content,
}: {
  draft: CharacterDraft
  patch: (p: Partial<CharacterDraft>) => void
  content: ContentIndex
}) {
  return (
    <div className="grid gap-2 sm:grid-cols-2">
      {Object.values(content.backgrounds).map((bg) => {
        const active = draft.backgroundId === bg.id
        return (
          <button
            key={bg.id}
            type="button"
            onClick={() => patch({ backgroundId: bg.id })}
            className={`cursor-pointer rounded border p-2 text-left transition-colors ${
              active
                ? 'border-gold bg-gold/20'
                : 'border-line-soft bg-surface-2 hover:border-gold-dim'
            }`}
          >
            <div className={`text-sm ${active ? 'text-gold-bright' : 'text-ink'}`}>{bg.name}</div>
            <div className="mt-1 flex flex-wrap gap-1">
              {bg.skillProficiencies.map((p) => (
                <Tag key={p} tone="gold">
                  {p.replace('skill.', '')}
                </Tag>
              ))}
              {bg.toolProficiencies.map((p) => (
                <Tag key={p}>{p.replace('tool.', '')}</Tag>
              ))}
            </div>
            {bg.features.length > 0 && (
              <div className="mt-1 text-[0.65rem] text-ink-faint">
                {bg.features.map((f) => f.name).join(', ')}
              </div>
            )}
          </button>
        )
      })}
    </div>
  )
}

// ===========================================================================
// ADIM 5 — SINIF + BECERİLER
// ===========================================================================

function StepSinif({
  draft,
  patch,
  content,
}: {
  draft: CharacterDraft
  patch: (p: Partial<CharacterDraft>) => void
  content: ContentIndex
}) {
  const already = useMemo(() => {
    const set = new Set<string>()
    const bg = draft.backgroundId ? content.backgrounds[draft.backgroundId] : null
    for (const p of bg?.skillProficiencies ?? []) set.add(p)
    const race = draft.raceId ? content.races[draft.raceId] : null
    for (const f of race?.features ?? [])
      for (const p of f.grantsProficiencies ?? []) if (p.startsWith('skill.')) set.add(p)
    for (const ids of Object.values(draft.choices))
      for (const id of ids) if (id.startsWith('skill.')) set.add(id)
    return [...set]
  }, [draft, content])

  const skills = evaluateSkillChoices(draft.classId, draft.skillProficiencies, already, content)

  const toggleSkill = (key: string) => {
    const current = draft.skillProficiencies
    if (current.includes(key)) {
      patch({ skillProficiencies: current.filter((k) => k !== key) })
    } else {
      patch({ skillProficiencies: [...current, key] })
    }
  }

  return (
    <div className="space-y-4">
      <div className="grid gap-2 sm:grid-cols-2">
        {Object.values(content.classes).map((cls) => {
          const active = draft.classId === cls.id
          return (
            <button
              key={cls.id}
              type="button"
              onClick={() => patch({ classId: cls.id, skillProficiencies: [] })}
              className={`cursor-pointer rounded border p-3 text-left transition-colors ${
                active
                  ? 'border-gold bg-gold/20'
                  : 'border-line-soft bg-surface-2 hover:border-gold-dim'
              }`}
            >
              <div className={`font-display text-base ${active ? 'text-gold-bright' : 'text-ink'}`}>
                {cls.name}
              </div>
              <div className="mt-1 flex flex-wrap gap-1 text-[0.65rem]">
                <Tag tone="gold">1d{cls.hitDie}</Tag>
                <Tag>kurtarma: {cls.savingThrowProficiencies.join(', ')}</Tag>
                {cls.spellcasting.mode !== 'none' && <Tag tone="arcane">büyücü</Tag>}
                <Tag>{cls.skillChoices} beceri</Tag>
              </div>
            </button>
          )
        })}
      </div>

      {draft.classId && (
        <div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-ink">
              Beceri Seçimi
              <span className="ml-2 text-[0.65rem] text-ink-faint">
                {skills.remaining} seçim kaldı
              </span>
            </span>
            <span className={`tnum text-[0.65rem] ${skills.complete ? 'text-vital' : 'text-warn'}`}>
              {draft.skillProficiencies.length}/
              {skills.complete ? 0 : skills.remaining + draft.skillProficiencies.length}
            </span>
          </div>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {skills.available.map((option) => {
              const active = draft.skillProficiencies.includes(option.key)
              const blocked = skills.blocked[option.key]
              return (
                <button
                  key={option.key}
                  type="button"
                  disabled={Boolean(blocked) && !active}
                  onClick={() => toggleSkill(option.key)}
                  title={blocked}
                  className={`cursor-pointer rounded border px-2 py-1 text-xs transition-colors ${
                    active
                      ? 'border-gold bg-gold/20 text-gold-bright'
                      : blocked
                        ? 'cursor-not-allowed border-line-soft bg-surface-2 text-ink-faint opacity-50'
                        : 'border-line-soft bg-surface-2 text-ink-muted hover:border-gold-dim'
                  }`}
                >
                  {option.nameTr}
                  {blocked && ' 🔒'}
                </button>
              )
            })}
          </div>
          {skills.available.some((o) => o.alreadyProficient) && (
            <p className="mt-1.5 text-[0.65rem] text-ink-faint">
              🔒 = zaten yetkin olduğun beceri (sınıf için yeniden seçilemez).
            </p>
          )}
        </div>
      )}
    </div>
  )
}

// ===========================================================================
// ADIM 6 — SEVİYE + ASI
// ===========================================================================

// ---------------------------------------------------------------------------
// ADIM 6 — SEVİYE + YETENEK GELİŞTİRME (ASI)
// ---------------------------------------------------------------------------

/**
 * Geliştirme slotlarını React'in `key` olarak kullanabileceği gerçek
 * kimliklerle donatır. Dizin anahtarı yerine kimlik kullanmak, liste
 * yeniden sıralandığında yanlış eşleşmeyi ve "neden bu geliştirme kaydı?" gibi
 * hataları önler.
 */
function asiRows(
  slots: { index: number; level: number }[],
  filled: AsiSlot[],
): { id: string; slot: AsiSlot; ordinal: number }[] {
  return slots.map((s, i) => ({
    id: `asi-slot-${s.level}-${i}`,
    slot: filled[i] ?? { index: i, level: s.level, target: null, amount: 1 },
    ordinal: i + 1,
  }))
}

function StepSeviye({
  draft,
  patch,
  content,
}: {
  draft: CharacterDraft
  patch: (p: Partial<CharacterDraft>) => void
  content: ContentIndex
}) {
  const slots = asiSlotsForLevel(draft.level, content, draft.classId)

  // Slot listesini seviyeye göre senkronla
  const asiSlots = slots.map(
    (slot, i) =>
      draft.asiSlots[i] ?? {
        index: i,
        level: slot.level,
        target: null,
        amount: 1 as const,
      },
  )

  const asiState = evaluateAsi(draft.abilityScores, draft.asiChoices, asiSlots)
  const rows = asiRows(slots, draft.asiSlots)

  return (
    <div className="space-y-4">
      <div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-ink-muted">Seviye</span>
          <span className="font-display text-2xl font-semibold text-gold">{draft.level}</span>
        </div>
        <input
          type="range"
          min={1}
          max={20}
          value={draft.level}
          onChange={(e) => {
            const level = Number(e.target.value)
            patch({ level, asiSlots: [] })
          }}
          className="mt-2 w-full accent-[var(--color-gold)]"
        />
        <div className="flex justify-between text-[0.65rem] text-ink-faint">
          <span>1</span>
          <span className="tnum">{xpForLevel(draft.level)} XP</span>
          <span>20</span>
        </div>
      </div>

      {rows.length > 0 && (
        <div>
          <div className="text-sm text-ink-muted">
            Yetenek Geliştirme ({asiState.used}/{asiState.total})
          </div>
          <div className="mt-1.5 space-y-2">
            {rows.map(({ id, slot, ordinal }, i) => (
              <div key={id} className="rounded border border-line-soft bg-surface-2 p-2">
                <div className="mb-1 text-[0.65rem] text-ink-faint">
                  {ordinal}. geliştirme (seviye {slot.level})
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {ABILITY_KEYS.map((key) => {
                    const active = slot.target === key
                    const final = asiState.finalScores[key] ?? 0
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => {
                          const next = [...asiSlots]
                          next[i] = { ...slot, target: active ? null : key }
                          patch({ asiSlots: next })
                        }}
                        className={`cursor-pointer rounded border px-2 py-1 text-xs transition-colors ${
                          active
                            ? 'border-gold bg-gold/20 text-gold-bright'
                            : 'border-line-soft bg-surface-3 text-ink-muted hover:border-gold-dim'
                        }`}
                      >
                        {ABILITY_NAMES_TR[key]} {final}
                      </button>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
          {asiState.errors.length > 0 && (
            <ul className="mt-1.5 space-y-0.5">
              {asiState.errors.map((e) => (
                <li key={e} className="text-[0.65rem] text-blood">
                  {e}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}

// ===========================================================================
// ADIM 7 — ÖZET
// ===========================================================================

function StepOzet({
  draft,
  content,
  preview,
}: {
  draft: CharacterDraft
  content: ContentIndex
  preview: ReturnType<typeof aggregateCharacter> | null
}) {
  const abilityCheck = validateAbilityScores(draft.abilityMethod, draft.abilityScores)
  const race = draft.raceId ? content.races[draft.raceId] : null
  const cls = draft.classId ? content.classes[draft.classId] : null
  const bg = draft.backgroundId ? content.backgrounds[draft.backgroundId] : null

  return (
    <div className="space-y-3">
      <div className="text-center">
        <h2 className="font-display text-2xl text-ink">{draft.name || '(isimsiz)'}</h2>
        <p className="mt-1 text-sm text-ink-muted">
          {[race?.name, cls?.name, bg?.name, draft.alignment].filter(Boolean).join(' · ')}
        </p>
      </div>

      {preview ? (
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
          {[
            { label: 'AC', value: preview.defense.armorClass },
            { label: 'HP', value: preview.defense.hitPoints },
            { label: 'İnisiyatif', value: signed(preview.defense.initiative) },
            { label: 'Hız', value: `${preview.defense.speed} ft` },
            { label: 'PB', value: signed(preview.proficiencyBonus) },
            {
              label: 'DC',
              value: preview.spellcasting.isSpellcaster ? preview.spellcasting.saveDc : '—',
            },
          ].map((s) => (
            <div
              key={s.label}
              className="rounded border border-line-soft bg-surface-2 py-2 text-center"
            >
              <div className="text-[0.6rem] tracking-[0.14em] text-ink-faint uppercase">
                {s.label}
              </div>
              <div className="tnum font-display text-xl text-gold-bright">{s.value}</div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-center text-sm text-warn">Önizleme için sınıf seçilmeli.</p>
      )}

      {/* Yetenekler */}
      <div className="grid grid-cols-3 gap-1.5 sm:grid-cols-6">
        {ABILITY_KEYS.map((key) => {
          const raceBonus = race?.abilityBonuses[key] ?? 0
          const asiBonus = draft.asiChoices[key] ?? 0
          const fromAsi = preview
            ? preview.abilities[key].score - draft.abilityScores[key] - raceBonus || 0
            : 0
          const total = draft.abilityScores[key] + raceBonus + asiBonus + fromAsi
          return (
            <div
              key={key}
              className="rounded border border-line-soft bg-surface-2 py-1.5 text-center"
            >
              <div className="text-[0.55rem] text-ink-faint">{ABILITY_NAMES_TR[key]}</div>
              <div className="tnum text-base text-ink">{total}</div>
              <div className="tnum text-[0.6rem] text-ink-muted">{mod(total)}</div>
            </div>
          )
        })}
      </div>

      {!abilityCheck.valid && (
        <p className="text-center text-xs text-blood">{abilityCheck.error}</p>
      )}

      {preview && preview.warnings.length > 0 && (
        <div className="rounded border border-warn/40 p-2">
          <div className="text-xs text-warn">Uyarılar</div>
          {preview.warnings.map((w) => (
            <div key={w} className="text-[0.65rem] text-ink-muted">
              • {w}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function signed(n: number): string {
  return n >= 0 ? `+${n}` : `${n}`
}

// ===========================================================================
// CANLI ÖNİZLEME PANELİ
// ===========================================================================

function Onizleme({
  preview,
  draft,
}: {
  preview: ReturnType<typeof aggregateCharacter>
  draft: CharacterDraft
}) {
  return (
    <div className="panel p-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="panel-title border-0 p-0">Canlı Önizleme</span>
        <span className="text-[0.65rem] text-ink-faint">sayıya tıkla → zar atılır</span>
      </div>
      <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
        {[
          { label: 'AC', value: preview.defense.armorClass, dc: null },
          { label: 'HP', value: preview.defense.hitPoints, dc: null },
          {
            label: 'İnisiyatif',
            value: signed(preview.defense.initiative),
            dc: preview.defense.initiative,
          },
          { label: 'Pasif Algı', value: preview.passivePerception, dc: null },
          {
            label: 'Büyü DC',
            value: preview.spellcasting.isSpellcaster ? preview.spellcasting.saveDc : '—',
            dc: null,
          },
          { label: 'PB', value: signed(preview.proficiencyBonus), dc: null },
        ].map((s) => (
          <button
            key={s.label}
            type="button"
            disabled={s.dc == null}
            onClick={() =>
              s.dc != null &&
              rollCheck(`${draft.name || 'Karakter'} — ${s.label}`, s.dc, {
                subtitle: `1d20 ${signed(s.dc)}`,
              })
            }
            className="rounded border border-line-soft bg-surface-2 py-1.5 text-center transition-colors enabled:cursor-pointer enabled:hover:border-gold-dim disabled:cursor-default"
          >
            <div className="text-[0.55rem] tracking-[0.14em] text-ink-faint uppercase">
              {s.label}
            </div>
            <div className="tnum font-display text-lg text-gold-bright">{s.value}</div>
          </button>
        ))}
      </div>
    </div>
  )
}
